/**
 * Endpoints del API de RoadIt
 * Enum con todos los endpoints disponibles del backend Django
 */

export enum EndPoints {
  // ======================
  // AUTENTICACIÓN
  // ======================
  login = "/login/", // POST LoginRequestDto -> LoginResponseDto
  logout = "/logout/", // POST LogoutRequestDto -> ApiResponse
  refreshToken = "/token/refresh/", // POST RefreshTokenRequestDto -> RefreshTokenResponseDto
  passwordRequestReset = "/users/password_request_reset/", // POST { email } -> { message }
  setPassword = "/users/set_password/", // POST { password, password2 } -> ApiResponse
  passwordResetConfirm = "/users/password-reset-confirm/", // POST { uid, token, password } -> ApiResponse

  // ======================
  // USUARIOS
  // ======================
  // Usuarios generales
  getUsers = "/users/", // GET -> PaginatedResponse<User>
  createUser = "/users/", // POST CreateUserRequestDto -> User
  getUser = "/users/{userId}/", // GET -> User
  updateUser = "/users/{userId}/", // PATCH UpdateUserRequestDto -> User
  deleteUser = "/users/{userId}/", // DELETE -> ApiResponse
  resetPassword = "/users/reset_password/", // POST ResetPasswordRequestDto -> ApiResponse
  changePassword = "/users/change_password/", // POST ChangePasswordRequestDto -> ApiResponse

  // Administradores
  getAdmins = "/admins/", // GET -> PaginatedResponse<User>
  createAdmin = "/admins/", // POST CreateUserRequestDto -> User
  getAdmin = "/admins/{adminId}/", // GET -> User
  updateAdmin = "/admins/{adminId}/", // PATCH UpdateUserRequestDto -> User
  deleteAdmin = "/admins/{adminId}/", // DELETE -> ApiResponse
  exportAdminsCSV = "/admins/export-csv/", // GET -> CSV file

  // Conductores
  getDrivers = "/drivers/", // GET -> PaginatedResponse<Driver>
  createDriver = "/drivers/", // POST CreateDriverRequestDto -> Driver
  getDriver = "/drivers/{driverId}/", // GET -> Driver
  updateDriver = "/drivers/{driverId}/", // PATCH UpdateDriverRequestDto -> Driver
  deleteDriver = "/drivers/{driverId}/", // DELETE -> ApiResponse
  exportDriversCSV = "/drivers/export-csv/", // GET -> CSV file
  driverBlackList = "/blacklists/driver/{driver_id}/", //GET → TE DA LA LISTA NEGRA DE UN CONDUCTOR
  driverAuthorizedDrivers = "/drivers/{driverId}/authorized-drivers/", //GET → OBTENER CONDUCTORES AUTORIZADOS DE UN CONDUCTOR

  // Usuarios de cliente
  getClientUsers = "/client-users/", // GET -> PaginatedResponse<ClientUser>
  createClientUser = "/client-users/", // POST CreateClientUserRequestDto -> ClientUser
  getClientUser = "/client-users/{clientUserId}/", // GET -> ClientUser
  updateClientUser = "/client-users/{clientUserId}/", // PATCH UpdateClientUserRequestDto -> ClientUser
  deleteClientUser = "/client-users/{clientUserId}/", // DELETE -> ApiResponse
  exportClientUsersCSV = "/client-users/export-csv/", // GET -> CSV file

  // ======================
  // CLIENTES
  // ======================
  getClients = "/clients/", // GET -> PaginatedResponse<Client>
  createClient = "/clients/", // POST CreateClientRequestDto -> Client
  getClient = "/clients/{clientId}/", // GET -> Client
  updateClient = "/clients/{clientId}/", // PATCH UpdateClientRequestDto -> Client
  deleteClient = "/clients/{clientId}/", // DELETE -> ApiResponse
  exportClientsCSV = "/clients/export-csv/", // GET -> CSV file
  importClientsExcel = "/clients/import-excel/", // POST multipart/form-data (ZIP) -> Import clients
  getClientsPendingInvoice = "/clients/pending-invoice/", // GET -> List of clients pending invoicing

  // Grupos de clientes
  getClientGroups = "/clients-group/", // GET -> PaginatedResponse<ClientGroup>
  createClientGroup = "/clients-group/", // POST CreateClientGroupRequestDto -> ClientGroup
  getClientGroup = "/clients-group/{clientGroupId}/", // GET -> ClientGroup
  updateClientGroup = "/clients-group/{clientGroupId}/", // PATCH UpdateClientGroupRequestDto -> ClientGroup
  deleteClientGroup = "/clients-group/{clientGroupId}/", // DELETE -> ApiResponse
  exportClientGroupsCSV = "/clients-group/export-csv/", // GET -> CSV file

  // Notificaciones de clientes
  getClientNotifications = "/clients/{clientId}/notifications/", // GET -> ClientNotificationsDto
  updateClientNotifications = "/clients/{clientId}/notifications/", // PUT UpdateClientNotificationsRequestDto -> ClientNotificationsDto

  // ======================
  // TRANSPORTES
  // ======================
  getTransports = "/transports/", // GET -> PaginatedResponse<Transport>
  createTransport = "/transports/", // POST CreateTransportRequestDto -> Transport
  getTransport = "/transports/{transportId}/details-to-edit/", // GET -> Transport
  getTransportDocuments = "/transports/{transportId}/documents/", // GET -> Transport
  saveTransportDocuments = "/transports/{transportId}/upload_document/", // GET -> Transport
  getTransportSummaryStart = "/transports/{transportId}/start-summary/", // GET -> Transport
  getTransportSummaryEnd = "/transports/{transportId}/end-summary/", // GET -> Transport
  getTransportSummaryTransit = "/transports/{transportId}/in-transit-summary/", // GET -> Transport
  getTransportIncidents = "/incidences/", // GET -> Transport
  exportIncidents = "/incidences/export-csv/",
  updateTransport = "/transports/{transportId}/", // PATCH UpdateTransportRequestDto -> Transport
  deleteTransport = "/transports/{transportId}/", // DELETE -> ApiResponse
  exportTransportsCSV = "/transports/export-csv/", // GET -> CSV file
  calculatePrice = "/transports/calculate-preview/", // GET -> CSV file
  // Acciones específicas de transporte
  swapLegPoints = "/transports/{transportId}/swap-legpoints/", // POST SwapLegPointsRequestDto -> ApiResponse
  getTransportHistory = "/transports/history/", // GET -> PaginatedResponse<Transport>
  exportTransportHistoryCSV = "/transports/export-history-csv/", // GET -> CSV file
  cancelTransport = "/transports/{transportId}/cancel-transport/", // POST CancelTransportRequestDto -> ApiResponse
  getCancelTransportInfo = "/transports/{transportId}/cancel-transport-info/", // GET -> CancelTransportInfo
  getCancelTransportAdjustmentsThresholds = "/transports/cancel-transport/adjustments_thresholds/", // GET -> AdjustmentsThresholdsDto
  finalizeTransport = "/transports/{transportId}/finalize/", // POST FinalizeTransportRequestDto -> ApiResponse
  upsertAdditionalServices = "/transports/{transportId}/additional-services/upsert/", // POST UpsertAdditionalServicesRequestDto -> ApiResponse
  changeLicensePlate = "/transports/{transportId}/change-license-plate/", // POST ChangeLicensePlateRequestDto -> ApiResponse
  blockMission = "/transports/{transportId}/block-mission/", // POST BlockMissionRequestDto -> ApiResponse
  duplicateTransport = "/transports/{transportId}/duplicate/", // POST DuplicateTransportRequestDto -> Transport
  addReturn = "/transports/{transportId}/add-return/", // POST AddReturnRequestDto -> ApiResponse
  unassignDriver = "/unassigned-drivers/{unnasignId}/", // POST UnassignDriverRequestDto -> ApiResponse
  unassignDriverPrice = "/transports/{transportId}/unassign-driver-price/", // GET -> UnassignDriverPriceResponseDto
  getTransportOverview = "/transports/{transportId}/overview/", // GET -> TransportOverviewDto
  getTransportTimeline = "/transports/{id}/timeline/", // GET -> TransportTimelineDto
  getVehicleCarfax = "/vehicles/carfax/{licensePlate}/", // GET -> VehicleCarfaxDto
  generateTransportPdf = "/transports/{transportId}/generate_imv_pdf/", // GET -> PDF file
  getDriversTransport = "/transports/{transportId}/all-time-drivers/", // GET -> TransportOverviewDto

  // Citas con cliente final (Admin)
  getTransportAppointment = "/transports/{transportId}/appointment/", // GET -> TransportAppointmentAdminDto
  bookTransportAppointment = "/transports/{transportId}/appointment/", // POST AdminAppointmentInputDto -> TransportAppointmentAdminDto
  updateTransportAppointment = "/transports/{transportId}/appointment/", // PATCH Partial<AdminAppointmentInputDto> -> TransportAppointmentAdminDto
  resendTransportAppointmentEmail = "/transports/{transportId}/appointment/resend/", // POST -> { email_sent_at: string }

  // Direcciones de transporte
  getTransportAddresses = "/transport-address/", // GET -> PaginatedResponse<TransportAddress>
  getTransportAddressesByIds = "/transport-address/get-by-ids/", // POST -> {"address_ids": number[]} -> TransportAddress[]
  createTransportAddress = "/transport-address/", // POST CreateTransportAddressRequestDto -> TransportAddress
  getTransportAddress = "/transport-address/{addressId}/", // GET -> TransportAddress
  updateTransportAddress = "/transport-address/{addressId}/", // PATCH UpdateTransportAddressRequestDto -> TransportAddress
  deleteTransportAddress = "/transport-address/{addressId}/", // DELETE -> ApiResponse

  // ======================
  // PRESUPUESTOS
  // ======================
  getBudgets = "/budgets/", // GET -> PaginatedResponse<Budget>
  getTopBudgets = "/transports/{transportId}/top-budgets/", // GET ?quantity=10 -> Budget[]
  createBudget = "/budgets/", // POST CreateBudgetRequestDto -> Budget
  getBudget = "/budgets/{budgetId}/", // GET -> Budget
  updateBudget = "/budgets/{budgetId}/", // PATCH UpdateBudgetRequestDto -> Budget
  deleteBudget = "/budgets/{budgetId}/", // DELETE -> ApiResponse
  calculateBudgetMargin = "/budgets/calculate-margin/", // POST CalculateMarginRequestDto -> CalculateMarginResponseDto
  acceptBudget = "/budgets/{budgetId}/accept/", // POST AcceptBudgetRequestDto -> Budget
  rejectBudgets = "/budgets/reject-budgets/", // POST RejectBudgetsRequestDto -> ApiResponse

  // ======================
  // NOTIFICACIONES Y CAMPAÑAS
  // ======================
  listDriversForNotification = "/notifications/list-drivers/{transportId}/", // GET -> NotificationDriverDto[]
  listDriversForGeneralNotifications = "/notifications/list-drivers/", // GET -> NotificationDriverDto[]
  sendCustomNotification = "/notifications/send_custom_notification/", // POST -> Send custom notification
  listNotificationCampaigns = "/notifications/list_notification_campaigns/", // GET -> PaginatedResponse<NotificationCampaign>
  getNotificationCampaignDetail = "/notifications/{campaignId}/campaign_detail/", // GET -> CampaignDetail
  updateScheduledNotification = "/notifications/{campaignId}/update_scheduled_notification/", // PATCH -> ApiResponse
  cancelScheduledNotification = "/notifications/{scheduledTaskId}/cancel_scheduled_notification/", // POST -> ApiResponse
  resendNotificationCampaign = "/notifications/{campaignId}/resend_notification/", // POST -> ApiResponse
  listMyNotifications = "/notifications/list_my_notifications/", // GET -> Notification[]
  listUnreadNotifications = "/notifications/list_unread_notifications/", // GET -> Notification[]
  markNotificationAsRead = "/notifications/{notificationId}/mark_my_notification_as_read/", // POST -> ApiResponse
  deleteNotification = "/notifications/{notificationId}/delete_notification/", // DELETE -> ApiResponse

  // ======================
  // CUENTAS DE FACTURACIÓN
  // ======================
  // Cuentas de conductor
  getDriverBillingAccounts = "/driver-billing-accounts/", // GET -> PaginatedResponse<DriverBillingAccount>
  createDriverBillingAccount = "/driver-billing-accounts/", // POST CreateDriverBillingAccountRequestDto -> DriverBillingAccount
  getDriverBillingAccount = "/driver-billing-accounts/{accountId}/", // GET -> DriverBillingAccount
  updateDriverBillingAccount = "/driver-billing-accounts/{accountId}/", // PATCH UpdateDriverBillingAccountRequestDto -> DriverBillingAccount
  deleteDriverBillingAccount = "/driver-billing-accounts/{accountId}/", // DELETE -> ApiResponse

  // Cuentas de cliente
  getClientBillingAccounts = "/client-billing-accounts/", // GET -> PaginatedResponse<ClientBillingAccount>
  createClientBillingAccount = "/client-billing-accounts/", // POST CreateClientBillingAccountRequestDto -> ClientBillingAccount
  getClientBillingAccount = "/client-billing-accounts/{accountId}/", // GET -> ClientBillingAccount
  updateClientBillingAccount = "/client-billing-accounts/{accountId}/", // PATCH UpdateClientBillingAccountRequestDto -> ClientBillingAccount
  deleteClientBillingAccount = "/client-billing-accounts/{accountId}/", // DELETE -> ApiResponse
  importBillingAccountsExcel = "/client-billing-accounts/import-excel/?client_id={clientId}", // POST multipart/form-data (Excel) -> Import billing accounts

  // ======================
  // REGLAS DE PRECIO
  // ======================
  // Reglas por scope (client/transport)
  getPriceRules = "/price-rules/{scope}/", // GET scope=client|transport -> PaginatedResponse<PriceRuleSet>
  createPriceRule = "/price-rules/{scope}/", // POST scope=client|transport CreatePriceRuleSetRequestDto -> PriceRuleSet
  getPriceRule = "/price-rules/{scope}/{clientId}", // GET scope=client|transport -> PriceRuleSet
  updatePriceRule = "/price-rules/{scope}/{ruleId}/", // PATCH scope=client|transport UpdatePriceRuleSetRequestDto -> PriceRuleSet
  deletePriceRule = "/price-rules/{scope}/{ruleId}/", // DELETE scope=client|transport -> ApiResponse

  // Brackets de distancia
  getDistanceBrackets = "/price-rules/{scope}/brackets/", // GET scope=client|transport -> PaginatedResponse<DistanceBracket>
  createDistanceBracket = "/price-rules/{scope}/brackets/", // POST scope=client|transport CreateDistanceBracketRequestDto -> DistanceBracket
  getDistanceBracket = "/price-rules/{scope}/brackets/{bracketId}/", // GET scope=client|transport -> DistanceBracket
  updateDistanceBracket = "/price-rules/{scope}/brackets/{bracketId}/", // PATCH scope=client|transport UpdateDistanceBracketRequestDto -> DistanceBracket
  deleteDistanceBracket = "/price-rules/{scope}/brackets/{bracketId}/", // DELETE scope=client|transport -> ApiResponse

  // ======================
  // SERVICIOS ADICIONALES
  // ======================
  // Servicios de transporte
  getTransportAdditionalServices = "/additional-services/transport/", // GET -> PaginatedResponse<TransportAdditionalService>
  createTransportAdditionalService = "/additional-services/transport/", // POST CreateTransportAdditionalServiceRequestDto -> TransportAdditionalService
  getTransportAdditionalService = "/additional-services/transport/{serviceId}/", // GET -> TransportAdditionalService
  updateTransportAdditionalService = "/additional-services/transport/{serviceId}/", // PATCH UpdateTransportAdditionalServiceRequestDto -> TransportAdditionalService
  deleteTransportAdditionalService = "/additional-services/transport/{serviceId}/", // DELETE -> ApiResponse

  // Servicios de cliente
  getClientAdditionalServices = "/additional-services/?client_id={clientId}", // GET -> ClientAdditionalService[]
  createClientAdditionalService = "/additional-services/", // POST
  updateClientAdditionalService = "/additional-services/{serviceId}/", // PATCH
  getClientAdditionalService = "/additional-services/client/{serviceId}/", // GET -> ClientAdditionalService
  deleteClientAdditionalService = "/additional-services/client/{serviceId}/", // DELETE -> ApiResponse
  getClientAdditionalServicesTemplates = "/additional-services-templates/", // GET -> AdditionalServiceDto[]
  // ======================
  // EXÁMENES
  // ======================
  getExams = "/exams/", // GET -> PaginatedResponse<Exam>
  createExam = "/exams/", // POST CreateExamRequestDto -> Exam
  getExam = "/exams/{examId}/", // GET -> Exam
  updateExam = "/exams/{examId}/", // PATCH UpdateExamRequestDto -> Exam
  deleteExam = "/exams/{examId}/", // DELETE -> ApiResponse

  // ======================
  // CERTIFICACIONES
  // ======================
  getCertifications = "/certifications/", // GET -> PaginatedResponse<Certification>
  createCertification = "/certifications/", // POST CreateCertificationRequestDto -> Certification
  getCertification = "/certifications/{certificationId}/", // GET -> Certification
  updateCertification = "/certifications/{certificationId}/", // PATCH UpdateCertificationRequestDto -> Certification
  deleteCertification = "/certifications/{certificationId}/", // DELETE -> ApiResponse
  createManualCertification = "/certifications/manual/", // POST CreateManualCertificationRequestDto -> Certification
  getCertificationByDriverAndClient = "/certifications/certification-by-client/{driverId}/", // GET -> PaginatedResponse<Certification>
  getDriverClientCertifications = "/certifications/driver-certifications/{driverId}/all-client-certifications/", // GET -> ClientCertificationsResponse[]
  bulkUpdateDriverCertifications = "/certifications/driver-certifications/bulk-update/{driverId}/", // POST BulkUpdateCertificationRequestDto[] -> ApiResponse
  // ======================
  // CERTIFICACIONES
  // ======================s
  getClientCertifications = "/certifications/client-certifications/",
  updateClientCertification = "/certifications/client-certifications/{certificationId}/", // PATCH UpdateCertificationRequestDto -> Certification
  deleteClientCertification = "/certifications/client-certifications/{id}/", // DELETE
  getCertificationsTemlates = "/certifications/templates-certifications/", // GET -> PaginatedResponse<Certifications>

  // ======================
  // PROTOCOLOS
  // ======================
  getProtocols = "/protocols/", // GET -> PaginatedResponse<Protocol>
  createProtocols = "/protocols/", // GET -> PaginatedResponse<Protocol>
  updateProtocols = "/protocols/{id}/", // GET -> PaginatedResponse<Protocol>
  deleteProtocols = "/protocols/{id}/", // GET -> PaginatedResponse<Protocol>

  // ======================
  // DOCUMENTOS DE CONDUCTOR
  // ======================
  getDriverDocuments = "/driver-documents/", // GET -> PaginatedResponse<DriverDocument>
  createDriverDocument = "/driver-documents/", // POST CreateDriverDocumentRequestDto -> DriverDocument
  getDriverDocument = "/driver-documents/{documentId}/", // GET -> DriverDocument
  updateDriverDocument = "/driver-documents/{documentId}/", // PATCH UpdateDriverDocumentRequestDto -> DriverDocument
  deleteDriverDocument = "/driver-documents/{documentId}/", // DELETE -> ApiResponse
  validateDriverDocument = "/driver-documents/{documentId}/validate/", // PUT ValidateDriverDocumentRequestDto -> ApiResponse
  invalidateDriverDocument = "/driver-documents/{documentId}/invalidate/", // PUT InvalidateDriverDocumentRequestDto -> ApiResponse
  exportDriverDocumentsCSV = "/driver-documents/export-csv/", // GET -> CSV file
  getDriverDocumentInvalidationReasons = "/driver-documents/invalidation-reasons/", // GET -> InvalidationReason[]

  // ======================
  // AJUSTES
  // ======================
  getAdjustments = "/adjustments/", // GET -> PaginatedResponse<Adjustment>
  createAdjustment = "/adjustments/", // POST CreateAdjustmentRequestDto -> Adjustment
  getAdjustment = "/adjustments/{adjustmentId}/", // GET -> Adjustment
  updateAdjustment = "/adjustments/{adjustmentId}/", // PATCH UpdateAdjustmentRequestDto -> Adjustment
  deleteAdjustment = "/adjustments/{adjustmentId}/", // DELETE -> ApiResponse

  // ======================
  // LISTAS NEGRAS
  // ======================
  getBlacklists = "/blacklists/", // GET -> PaginatedResponse<BlackList>
  createBlacklist = "/blacklists/", // POST CreateBlackListRequestDto -> BlackList
  getBlacklist = "/blacklists/{blacklistId}/", // GET -> BlackList
  updateBlacklist = "/blacklists/{blacklistId}/", // PATCH UpdateBlackListRequestDto -> BlackList
  deleteBlacklist = "/blacklists/{blacklistId}/", // DELETE -> ApiResponse
  deleteGroupBlacklist = "/blacklists/delete-group/", // POST {driver: id, client: id} -> ApiResponse

  // ======================
  // INCIDENCIAS
  // ======================
  getIncidences = "/incidences/", // GET -> PaginatedResponse<Incidence>
  createIncidence = "/incidences/", // POST CreateIncidenceRequestDto -> Incidence
  
  getIncidence = "/incidences/{incidenceId}/", // GET -> Incidence
  updateIncidence = "/incidences/{incidenceId}/", // PATCH UpdateIncidenceRequestDto -> Incidence
  addCommentIncidence = "/incidences/{incidenceId}/add-comment/", // POST {comment: string} -> Incidence
  deleteIncidence = "/incidences/{incidenceId}/", // DELETE -> ApiResponse
  getIncidenceSubtypes = "/incidences/subtypes/", // GET -> IncidenceSubtype[]
  getIncidenceMetrics = "/incidences/{incidenceId}/incidence-metrics/", // GET -> IncidenceMetrics
  getPrevieweRating = "/incidences/preview-rating/", // POST 
  calculateAmmount = "/adjustments/adjustment-template/?reason={reason}&sub_reason={reasonSubType}&quantity={quantity}&transport_id={transportId}", // GET -> IncidenceMetrics


  // ======================
  // PLANTILLAS DE DOCUMENTOS
  // ======================
  getDocumentTemplates = "/document-templates/", // GET -> PaginatedResponse<DocumentTemplate>
  createDocumentTemplate = "/document-templates/", // POST CreateDocumentTemplateRequestDto -> DocumentTemplate
  getDocumentTemplate = "/document-templates/{templateId}/", // GET -> DocumentTemplate
  updateDocumentTemplate = "/document-templates/{templateId}/", // PATCH UpdateDocumentTemplateRequestDto -> DocumentTemplate
  deleteDocumentTemplate = "/document-templates/{templateId}/", // DELETE -> ApiResponse
  getDocumentTemplatesByClient = "/document-templates/by-client/{clientId}/", // GET -> PaginatedResponse<DocumentTemplate>
  getImportDataUrls = "/utils/file-templates/", // GET -> ImportDataUrlsDto
  getBadgetCounters = "/utils/badge-counters/", // GET -> BudgetCountersDto
  // ======================
  // ETIQUETAS
  // ======================
  getTags = "/tags/", // GET -> PaginatedResponse<Tag>
  createTag = "/tags/", // POST CreateTagRequestDto -> Tag
  getTag = "/tags/{tagId}/", // GET -> Tag
  updateTag = "/tags/{tagId}/", // PATCH UpdateTagRequestDto -> Tag
  deleteTag = "/tags/{tagId}/", // DELETE -> ApiResponse

  // ======================
  // CONFIGURACIÓN
  // ======================
  getDefaultTaxes = "/config/default-taxes/", // GET -> DefaultTaxesDto
  updateDefaultTaxes = "/config/default-taxes/", // POST UpdateDefaultTaxesRequestDto -> ApiResponse

  // ======================
  // ARCHIVOS ESTÁTICOS
  // ======================
  getMediaFile = "/media/{path}", // GET -> File
  getAPISchema = "/schema/", // GET -> OpenAPI Schema
  getSwaggerDocs = "/docs/", // GET -> Swagger UI

  // ======================
  // FESTIVOS
  // ======================
  holidays = "/holidays/", // GET GetHolidaysParams -> GetHolidaysResponse

  // ======================
  // GOOGLE PLACE
  // ======================
  addressByGoogle = "/transports/address-autocomplete/?query={query}", // POST
  addressByPlaceId = "/transports/address/details/{google_id}/", // POST

  // ======================
  // FACTURAS DE CONDUCTORES
  // ======================
  getDriversForInvoicing = "/invoices/drivers/", // GET -> PaginatedResponse<DriverForInvoicing>
  getDriversInfoToInvoice = "/invoices/drivers-info-to-invoice/", // GET -> DriverInvoiceInfo[] with driver_ids query param
  getDriverInvoices = "/invoices/drivers-invoices/", // GET -> PaginatedResponse<DriverInvoice>
  getDriverInvoice = "/invoices/drivers-invoices/{invoiceId}/", // GET -> DriverInvoice
  exportDriverInvoicesCSV = "/invoices/drivers-invoices/export-csv/", // GET -> CSV file
  changeInvoicesStatus = "/invoices/change-status/", // PATCH -> Change invoice status
  createDriverInvoicePreview = "/invoices/create-driver-preview/", // POST -> Preview driver invoice totals
  createDriverInvoice = "/invoices/create-driver/", // POST -> Create driver invoices from selected transports/adjustments

  // ======================
  // FACTURAS DE CLIENTES
  // ======================
  getClientInvoices = "/invoices/clients-invoices/", // GET -> PaginatedResponse<ClientInvoice>
  getClientInvoice = "/invoices/clients-invoices/{invoiceId}/", // GET -> ClientInvoice
  exportClientInvoicesCSV = "/invoices/clients-invoices/export-csv/", // GET -> CSV file
  createClientInvoices = "/invoices/create-client/", // POST -> Create client invoices from selected clients

  // ======================
  // FACTURAS (COMPARTIDO)
  // ======================
  getInvoiceTransportDetail = "/invoices/{invoiceTransportId}/transport/", // GET -> InvoiceTransport detail
  getInvoiceDetail = "/invoices/{invoiceId}/", // GET -> Invoice detail (driver or client)
  updateInvoice = "/invoices/{invoiceId}/", // PATCH -> Partial update invoice
  exportInvoicesBankCSV = "/invoices/export-bank-csv/", // POST ?type=driver|client -> CSV file for bank
  exportInvoicesInternalCSV = "/invoices/export-internal-csv/", // POST ?type=driver|client -> CSV file for internal use
  exportInvoicesPDF = "/invoices/export-pdf/",               // POST -> PDF file | { id, file_id, status }
  exportInvoicesPDFResult = "/invoices/export-pdf/{exportId}/", // GET -> Blob | json status

  // ======================
  // GASTOS ADICIONALES
  // ======================
  getAdditionalCosts = "/additional-costs/", // GET -> PaginatedResponse<AdditionalCost>
  createAdditionalCost = "/additional-costs/", // POST CreateAdditionalCostRequestDto -> AdditionalCost
  getAdditionalCost = "/additional-costs/{additionalCostId}/", // GET -> AdditionalCost
  updateAdditionalCost = "/additional-costs/{additionalCostId}/", // PATCH UpdateAdditionalCostRequestDto -> AdditionalCost
  deleteAdditionalCost = "/additional-costs/{additionalCostId}/", // DELETE -> ApiResponse
  exportAdditionalCostsCSV = "/additional-costs/export-csv/", // GET -> CSV file
  getAdditionalCostsRejectionTypes = "/additional-costs/rejection-types/", // GET -> RejectionType[]
  rejectAdditionalCostRequest = "/additional-costs/{additionalCostId}/reject-request/", // POST -> ApiResponse
  rejectAdditionalCostTicket = "/additional-costs/{additionalCostId}/reject-ticket/", // POST -> ApiResponse
  approveAdditionalCostRequest = "/additional-costs/{additionalCostId}/approve-request/", // POST -> ApiResponse
  approveAdditionalCostTicket = "/additional-costs/{additionalCostId}/approve-ticket/", // POST -> ApiResponse

  // ======================
  // ENCUESTAS (SURVEYS)
  // ======================
  getTransportSurveyAnswers = "/surveys/transport/{transportId}/answers/{phase}/", // GET -> SurveyAnswerItemDto[]

  // ======================
  // DASHBOARD
  // ======================
  DASHBOARD_ADMIN_SUMMARY = "/dashboard/admin/summary/",
  DASHBOARD_ADMIN_REVENUE_EVOLUTION = "/dashboard/admin/revenue-evolution/",
  DASHBOARD_CLIENT_SUMMARY = "/dashboard/client/summary/",
  DASHBOARD_CLIENT_EXPENSES_EVOLUTION = "/dashboard/client/expenses-evolution/",
}

/**
 * Tipos de HTTP methods utilizados en el API
 */
export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE'
}

/**
 * Utilidad para construir URLs con paráfos
 * @param endpoint El endpoint del enum
 * @param params Objeto con los parámetros a reemplazar
 * @returns URL construida con los parámetros
 *
 * @example
 * buildUrl(EndPoints.getUser, { userId: '123' })
 * // Resultado: "/users/123/"
 *
 * buildUrl(EndPoints.getPriceRules, { scope: 'client' })
 * // Resultado: "/price-rules/client/"
 */
export function buildUrl(endpoint: EndPoints, params?: Record<string, string | number>): string {
  let url = endpoint as string;

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
  }

  return url;
}

/**
 * Utilidad para extraer parámetros de una URL endpoint
 * @param endpoint El endpoint del enum
 * @returns Array con los nombres de los parámetros
 *
 * @example
 * getUrlParams(EndPoints.getUser)
 * // Resultado: ['userId']
 *
 * getUrlParams(EndPoints.getPriceRule)
 * // Resultado: ['scope', 'ruleId']
 */
export function getUrlParams(endpoint: EndPoints): string[] {
  const matches = (endpoint as string).match(/\{([^}]+)\}/g);
  if (!matches) return [];

  return matches.map(match => match.slice(1, -1));
}

/**
 * Utilidad para validar que se proporcionaron todos los parámetros requeridos
 * @param endpoint El endpoint del enum
 * @param params Objeto con los parámetros proporcionados
 * @returns true si todos los parámetros están presentes
 */
export function validateParams(endpoint: EndPoints, params?: Record<string, string | number>): boolean {
  const requiredParams = getUrlParams(endpoint);

  if (requiredParams.length === 0) {
    return true;
  }

  if (!params) {
    return false;
  }

  return requiredParams.every(param => params[param] !== undefined);
}
